// Акцент раздаётся значениями, а не через var() в inline-стилях.
//
// Замерено в Chrome, полный пересчёт стилей на 3000 узлах:
//
//   правило в таблице стилей, литерал    2.51 мс
//   правило в таблице стилей, var()      2.26 мс
//   inline-стиль, литерал                5.39 мс
//   inline-стиль, var()                 13.53 мс
//
// То есть в таблице стилей подстановка не стоит ничего — правило одно на всех, и
// значение считается один раз. А в inline-стиле она стоит вдвое с половиной
// дороже: вычисленный стиль такого элемента нельзя разделить с соседями, и
// подстановка идёт заново на каждом узле. Панель рисует inline-стилями почти всё,
// поэтому цвет акцента живёт здесь, а var(--accent-*) остаётся только в
// index.css, где он бесплатен.
//
// Смена темы перерисовывает дерево целиком (memo в панели нет), поэтому объект A
// правится на месте, а не заменяется: все, кто уже его импортировал, видят новые
// значения на ближайшей отрисовке.

// Не штатный индиго Tailwind, а свой: светлее и чуть синее. Штатный узнаётся
// с первого взгляда и делает панель похожей на собранную из шаблона.
const INDIGO = {
  50: "#f0f1ff",
  100: "#e3e5ff",
  200: "#c9ccff",
  300: "#a9adff",
  400: "#8a90fe",
  500: "#6c73f4",
  600: "#5a60e6",
  700: "#4a4fc9",
  800: "#3d41a3",
  900: "#33377f"
};

// Приглушённый красный, а не сигнальный: иначе он спорил бы с красным ошибок и
// удаления, который в панели остаётся своим, более светлым.
const CRIMSON = {
  50: "#fdf2f2",
  100: "#fbe2e2",
  200: "#f5c6c6",
  300: "#eb9e9e",
  400: "#e07c7c",
  500: "#d65f5f",
  600: "#c04a4a",
  700: "#9e3a3a",
  800: "#7d2f2f",
  900: "#5c2323"
};

// Мягкий зелёный — по той же мерке, что красный: тусклее и темнее сигнального
// зелёного «онлайн» и «успешно» (#4ade80), чтобы акцент с ним не путался.
const GREEN = {
  50: "#eff8f3",
  100: "#d7efe2",
  200: "#b0dec6",
  300: "#84c9a6",
  400: "#5bb38a",
  500: "#3a8f69",
  600: "#2f7a58",
  700: "#276449",
  800: "#20503b",
  900: "#1a4030"
};

// Белая — монохромный акцент на тёмной теме: основной цвет чуть приглушённый
// белый (чистый #fff на почти чёрном режет глаз), светлые ступени — до чистого
// белого, тёмные — серые для наведения, нажатия и концов градиентов.
const WHITE = {
  50: "#ffffff",
  100: "#fdfdfe",
  200: "#fafbfc",
  300: "#f7f8fa",
  400: "#f3f4f7",
  500: "#eceef2",
  600: "#d2d5db",
  700: "#a8acb5",
  800: "#737883",
  900: "#4a4e57"
};

// Бордовый — глубокий тёмно-красный. Основной цвет (500) очень тёмный: он для
// заливок — кнопок, плашек, своих сообщений; текст и значки берут светлые
// ступени, иначе на тёмном фоне их не прочитать.
const MAROON = {
  50: "#fcecec",
  100: "#f6d4d4",
  200: "#e89a9a",
  300: "#d05050",
  400: "#a32020",
  500: "#660000",
  600: "#570000",
  700: "#470000",
  800: "#380000",
  900: "#2a0000"
};

const RAMPS = { indigo: INDIGO, crimson: CRIMSON, green: GREEN, white: WHITE, maroon: MAROON };

const STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

const toRgb = (hex) => {
  const value = String(hex).replace("#", "");
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16)
  ];
};

const RGB = {};
Object.entries(RAMPS).forEach(([name, ramp]) => {
  RGB[name] = {};
  STEPS.forEach((step) => { RGB[name][step] = toRgb(ramp[step]).join(", "); });
});

let currentName = "indigo";

// Стабильная ссылка: её импортируют по всей панели, и заменять объект нельзя.
export const A = { ...INDIGO };

// Строки с прозрачностью собираются один раз на пару «ступень + альфа» и дальше
// берутся из кеша: во время отрисовки они запрашиваются тысячами.
const alphaCache = new Map();

export const aa = (step, alpha) => {
  const key = `${currentName}|${step}|${alpha}`;
  const cached = alphaCache.get(key);
  if (cached) return cached;
  const value = `rgba(${RGB[currentName][step]}, ${alpha})`;
  alphaCache.set(key, value);
  return value;
};

// Смесь двух цветов #rrggbb: t = 0 — первый, t = 1 — второй. Для плотных
// оттенков акцента, которые должны оставаться непрозрачными (плашки поверх и
// тёмной, и белой карточки), — полупрозрачный aa() на белом бы выцвел.
export const mixHex = (from, to, t) => {
  const a = toRgb(from);
  const b = toRgb(to);
  const k = Math.max(0, Math.min(1, Number(t) || 0));
  return `#${a.map((value, index) => Math.round(value + (b[index] - value) * k).toString(16).padStart(2, "0")).join("")}`;
};

// Цвет текста и значков на сплошной заливке акцентом. У цветных акцентов это
// тот цвет, что стоял на месте (почти всегда белый), — он передаётся сюда и
// возвращается как есть. У белой темы акцент сам светлый, и на нём пишется
// тёмным. В index.css то же самое — переменная --accent-on.
const ON_ACCENT = { white: "#0e1015" };

export const onAccent = (color = "#ffffff") => ON_ACCENT[currentName] || color;

// Акцент светлый (белая тема): там, где на заливке акцентом лежит не просто
// текст, а целый блок — своё сообщение с временем, переводом и кнопками, —
// проще один раз спросить это, чем заворачивать каждое значение.
export const isLightAccent = () => Boolean(ON_ACCENT[currentName]);

export const setAccentMode = (mode) => {
  const name = RAMPS[mode] ? mode : "indigo";
  if (name === currentName) return;
  currentName = name;
  Object.assign(A, RAMPS[name]);
};

export const getAccentMode = () => currentName;

// --- Поверхности -----------------------------------------------------------
//
// Тёмная тема рисует полотно и карточки почти чёрными, графит — тёмно-серыми.
// Раньше это жило прямо в inline-стилях значениями, и графиту доставалось лишь
// то, что удалось перекрыть в css через !important по классу: у половины
// панелей класса нет, и она оставалась чёрной. Теперь оттенок берётся отсюда, и
// графит меняет его в одном месте.
//
// Светлой темы это не касается: у неё свои значения в тех же ветках, и в этой
// таблице её нет — для неё поверхности остаются такими же, как были у тёмной,
// то есть ровно как до правки.
const SURFACES = {
  // Ступени заметные, а не на четыре единицы: глубину дают тон и свет, а не
  // рамка вокруг каждой карточки.
  dark: { bg: "#0b0c0f", surface: "#121418", elevated: "#16191e", rgb: "18, 20, 24" },
  // Светлая тема перекрашивает тёмные поверхности правилами index.css, которые
  // узнают их по значению (rgb(14, 16, 21) и соседние). Ей отдаются прежние
  // тёмные значения, чтобы эти правила продолжали срабатывать.
  light: { bg: "#0a0b0f", surface: "#0e1015", elevated: "#101218", rgb: "14, 16, 21" },
  graphite: { bg: "#1c1f23", surface: "#262a30", elevated: "#2f3339", rgb: "38, 42, 48" }
};

let currentSurface = SURFACES.dark;

export const S = { ...SURFACES.dark };

const surfaceAlphaCache = new Map();

export const sa = (alpha) => {
  const key = `${currentSurface.rgb}|${alpha}`;
  const cached = surfaceAlphaCache.get(key);
  if (cached) return cached;
  const value = `rgba(${currentSurface.rgb}, ${alpha})`;
  surfaceAlphaCache.set(key, value);
  return value;
};

export const setSurfaceMode = (mode) => {
  const next = SURFACES[mode] || SURFACES.dark;
  if (next === currentSurface) return;
  currentSurface = next;
  Object.assign(S, next);
};
